/**
 * Admin — Layout route for the tenant-operations console.
 *
 * Seven sub-tabs (after Users and clearance-management moved to /users, and
 * Trust owns audit/compliance evidence, and AUTO-8 added Discover):
 *   /admin/discover       — Browse the system catalog (tools/skills/prompts/...) (AUTO-8)
 *   /admin/settings       — General, Compliance, API, Integrations, Data
 *   /admin/security       — Authentication, Session, Privileged, Keys, Threat
 *   /admin/health         — Overview, Services, Infra, Pipeline, Diagnostics, Maintenance
 *   /admin/notifications  — Rules, Channels, Escalation, Subscribers, Delivery Log
 *   /admin/audit          — Timeline, Change Sets, Integrity (admin-scoped mirror of Trust/Audit)
 *   /admin/branding       — White-label (existing, unchanged)
 *
 * Separation:
 *   - Users (own rail item) owns user lifecycle + RBAC + clearance
 *   - Trust (own rail item) owns compliance frameworks + tenant-wide hash-chained audit
 *   - Admin owns tenant CONFIGURATION + infrastructure + the admin-scoped audit view +
 *     the catalog Discover surface (AUTO-8)
 *
 * Legacy redirects:
 *   /admin       → /admin/settings (Settings is most cross-linked)
 *   /admin/users → /users
 */

import { createFileRoute, Outlet, useLocation, Navigate } from '@tanstack/react-router';
import { ContentHeader } from '@/components/content-header';
import {
  Lock, Palette, HeartPulse, Settings, Bell, ScrollText, Compass,
} from 'lucide-react';

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});

function AdminLayout() {
  const location = useLocation();

  // Redirect /admin to Settings (the most-linked page)
  if (location.pathname === '/admin' || location.pathname === '/admin/') {
    return <Navigate to="/admin/settings" />;
  }

  // Redirect legacy /admin/users to /users (moved to own rail item)
  if (location.pathname === '/admin/users' || location.pathname.startsWith('/admin/users/')) {
    return <Navigate to="/users" />;
  }

  return (
    <div className="flex flex-col h-full">
      <ContentHeader
        title="Admin"
        tabs={[
          { label: 'Discover',      path: '/admin/discover',      icon: Compass },
          { label: 'Settings',      path: '/admin/settings',      icon: Settings },
          { label: 'Security',      path: '/admin/security',      icon: Lock },
          { label: 'Health',        path: '/admin/health',        icon: HeartPulse },
          { label: 'Notifications', path: '/admin/notifications', icon: Bell },
          { label: 'Audit',         path: '/admin/audit',         icon: ScrollText },
          { label: 'Branding',      path: '/admin/branding',      icon: Palette },
        ]}
      />
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
